// Renders a single SERVICES entry (services-data.js) into `root`. Same
// split as project-template.js/PROJECTS: one shared template, one data
// object per page. No motion.js/Lenis — still the lighter project-page
// motion budget (reveal.js fades + plain CSS transitions) for everything
// except the What's Included stepped index, which pulls in bare GSAP +
// ScrollTrigger (see initScrollIndex's comment). The Process beats are
// static, no scroll-pin — deliberately a different mechanism, see that
// section's own comment.

// Shopify note: no subfolders in theme assets — every service image was
// flattened+renamed to service-{slug}-{base}-{width}.jpg at migration time
// (shopify-export/asset-rename-map.tsv in the original project). `path`
// below is now a full filename PREFIX (ending in "-", not "/") served from
// window.SHOPIFY_ASSET_BASE (the CDN prefix, set once in theme.liquid).
function serviceImgTag(slug, image, opts = {}) {
  return imgTagFromPath(`${window.SHOPIFY_ASSET_BASE}service-${slug}-`, image, opts);
}

function imgTagFromPath(path, image, { className = "", loading = "lazy", fetchpriority = "" } = {}) {
  const widths = image.widths;
  const largest = widths[widths.length - 1];
  const srcset = widths.map((w) => `${path}${image.base}-${w}.jpg ${w}w`).join(",\n            ");
  const fp = fetchpriority ? ` fetchpriority="${fetchpriority}"` : "";
  // Optional per-image crop bias (image.pos, e.g. "50% 70%") — several
  // frames on this page are much wider/shorter than their portrait source
  // photos (Beat 3's 21:9 in particular), and a plain center object-fit
  // crop can cut out the actual subject (confirmed live: a styling-in-
  // progress photo lost the hands/pillows entirely, keeping just the
  // person's shoulder). Defaults to unset (center, CSS's own default).
  const posStyle = image.pos ? ` style="object-position:${image.pos}"` : "";
  return `<img class="${className}"${posStyle}
      src="${path}${image.base}-${largest}.jpg"
      srcset="${srcset}"
      sizes="${image.sizes}"
      loading="${loading}"${fp}
      alt="${image.alt}">`;
}

function renderService(service, root) {
  const slug = service.slug;
  const headingTitles = { residential: "Luxury Residential Interior Design in Tampa Bay", commercial: "Commercial Interior Design in Tampa Bay", "virtual-design": "Virtual Interior Design", "investment-rental": "Investment &amp; Rental Interior Design in Tampa Bay", "new-development": "New Construction Interior Design in Tampa Bay" };

  const hero = `
<header class="hero hero--project hero--service" id="hero">
  ${serviceImgTag(slug, service.heroImage, { className: "hero-img", loading: "eager", fetchpriority: "high" })}
  <div class="hero-content">
    <h1 class="hero-title" style="font-size:clamp(38px,6vw,88px);line-height:1.12;max-width:1100px;margin-left:auto;margin-right:auto;">${headingTitles[slug] || service.name}</h1>
    <div class="hero-sub">${service.tagline}</div>
  </div>
  <span class="scroll-cue">Scroll</span>
</header>`;

  // Three static, individually-composed beats — deliberately NOT the
  // pinned-image/scroll-list pattern What's Included uses below. Explicit
  // user call: the two sections should read as genuinely different
  // layouts, not two instances of the same "image left, text right"
  // mechanic, and 3 items don't need a scroll-driven index the way 8 do.
  // Each beat gets its own composition (image side alternates, Beat 3
  // stacks instead of splitting) — same "no two compositions the same"
  // rule the homepage's own Process beats already follow. Copy is
  // unabridged (still the full bullets + closing line) but type is sized
  // down from the pinned-list version, since these no longer have a full
  // viewport-height of scroll runway to breathe in. reveal-el (the fade
  // from blurred/transparent) lives on the image only, not the text — per
  // explicit request, the text should just be there, readable, not ease
  // in from faint on scroll.
  // act.images (array of 3) is an alternate media slot to act.image
  // (single) — renders a "postcard stack" instead of one framed photo,
  // reusing the homepage's own Process Beat 2 .moodboard-card technique
  // verbatim (pinned card, shadow, pin-dot, tilt, caption; see main.css
  // PROCESS BEAT 2) rather than inventing a new mechanism. Each card's
  // aspect-ratio is set inline from image.ratio (the source photo's own
  // native ratio, object-fit:contain, no cropping) since .mb-top/.mb-1/
  // .mb-2's own hardcoded ratios in main.css are tuned to the homepage's
  // specific photos, not these.
  const renderActMedia = (act) => {
    if (!act.images) {
      return `<div class="act-beat-media reveal-el">${serviceImgTag(slug, act.image, { className: "" })}</div>`;
    }
    const [top, one, two] = act.images;
    const card = (img, cardClass) => `
      <figure class="moodboard-card ${cardClass}">
        <div class="moodboard-img-clip" style="aspect-ratio:${img.ratio}">${serviceImgTag(slug, img, { className: "" })}</div>
        <figcaption>${img.caption}</figcaption>
      </figure>`;
    return `
    <div class="act-postcards reveal-el">
      ${card(top, "mb-top")}
      <div class="moodboard-bottom-row">
        ${card(one, "mb-1")}
        ${card(two, "mb-2")}
      </div>
    </div>`;
  };

  const process = `
<section class="service-process">
  ${service.acts.map((act, i) => {
    const num = String(i + 1).padStart(2, "0");
    const bullets = (act.bullets || []).map((b) => `<li>${b}</li>`).join("");
    return `
  <div class="act-beat act-beat-${i + 1}">
    ${renderActMedia(act)}
    <div class="act-beat-text">
      <span class="act-beat-num">${num}</span>
      <h2 class="act-beat-label">${slug === "residential" ? ["In-Home Design Consultation", "Residential Design, Selections &amp; Procurement", "Installation &amp; Interior Styling"][i] : act.label}</h2>
      <p class="act-beat-body">${act.body}</p>
      ${bullets ? `<ul class="act-beat-list">${bullets}</ul>` : ""}
      ${act.closing ? `<p class="act-beat-closing">${act.closing}</p>` : ""}
    </div>
  </div>`;
  }).join("")}
</section>`;

  // Yodezeen-style stepped index (yodezeen.com/services/restaurants), adapted:
  // theirs auto-advances on a timer, ours is scroll-driven like the Process
  // section above (same initScrollIndex helper, just a taller list). Only
  // the active item expands (name promotes, subtitle/body/hook drop in via
  // a grid-template-rows 0fr→1fr transition — see CSS); everything else
  // stays collapsed to a single ghosted line, so an 8-item list doesn't
  // read as a wall of text.
  const includedItems = service.subservices.map((sub, i) => `
    <div class="included-item${i === 0 ? " is-active" : ""}" data-idx="${i}">
      <div class="included-item-head">
        <span class="included-item-marker"></span>
        <h3 class="included-item-name">${sub.name}</h3>
      </div>
      <div class="included-item-img">${serviceImgTag(slug, sub.image, { className: "" })}</div>
      <div class="included-item-detail-wrap">
        <div class="included-item-detail-inner">
          <p class="included-item-subtitle">${sub.subtitle}</p>
          <p class="included-item-body">${sub.body}</p>
          <p class="included-item-hook">${sub.hook}</p>
        </div>
      </div>
    </div>`).join("");

  const includedImages = service.subservices.map((sub, i) => `
    <div class="included-img${i === 0 ? " is-active" : ""}" data-img-idx="${i}">
      ${serviceImgTag(slug, sub.image, { className: "" })}
    </div>`).join("");

  const included = `
<section class="service-included">
  <div class="included-head"><h2 class="section-label reveal-el">What's Included</h2></div>
  <div class="included-layout">
    <div class="included-col">
      <div class="included-sticky">
        <div class="included-imgs">${includedImages}</div>
      </div>
    </div>
    <div class="included-list" style="--included-count:${service.subservices.length}">${includedItems}</div>
  </div>
  <div class="service-included-cta-fade" id="service-included-cta-fade" aria-hidden="true"></div>
</section>`;

  // `service.lead` used to render here as a standalone closing statement
  // (before that, right after the hero). Cut entirely per explicit
  // feedback — it read as an orphaned line wherever it sat, and the
  // wording (essentially "we design residences that feel like you") was
  // already restating the hero's own tagline. Not needed: the Process
  // beats and Included copy already carry the specific version of that
  // promise.

  // A text-link "Explore More Services" section used to live here, then a
  // plain project-card grid, then a drag-scroll related-projects carousel,
  // then the homepage's own "editorial featured/supporting" gallery
  // (renderRelatedWork/initRelatedWork). That last one was cut too per
  // explicit feedback: service pages should end on the CTA band
  // immediately after "What's Included," not send a reader who just
  // finished reading toward a browse-more-work detour first.

  root.innerHTML = hero + process + included;
  initScrollIndex({ itemSel: ".included-item", imgSel: ".included-img", listSel: ".included-list", stickySel: ".included-sticky" }, root);
  initServiceCtaFade(root);

  // Same recipe as project-template.js's renderProject — Shopify's own
  // page_title (the Page's Title field in the admin) only ever renders
  // the bare short name ("Residential"), not the fuller SEO-friendly
  // title each service page originally shipped with as static per-page
  // HTML ("Residential Interior Design — Sixth Sense Studio"). pageTitle
  // is the exact original title text (services-data.js); falls back to
  // the bare name if a future service is ever added without one.
  document.title = slug === "residential"
    ? "Luxury Residential Interior Design Tampa Bay | Sixth Sense Studio"
    : `${service.pageTitle || service.name} — Sixth Sense Studio`;

  // The CTA band itself lives in the page's own <footer>, outside `root` —
  // shared static markup duplicated across every page (this site has no
  // template system, see CLAUDE.md), so this is the one line of it that
  // becomes page-specific instead of the generic "Tell us your vision."
  // Every future service page gets its own via the same service.ctaLine
  // field, no extra wiring needed per page.
  const ctaKicker = document.querySelector(".cta-band .kicker");
  if (ctaKicker && service.ctaLine) ctaKicker.textContent = service.ctaLine;
}

// Drives the What's Included stepped index (the Process beats above are
// static, no scroll-pin — see the comment where `process` is built).
//
// Active-item detection: plain scroll listener, not IntersectionObserver
// thresholds. A fixed visibility-ratio threshold is fragile across varying
// content heights — measured live, a 0.6 threshold + shrunk rootMargin
// left real dead zones mid-scroll where NO item crossed the ratio, so
// nothing was marked active. "Whichever item's own center sits closest to
// the viewport's own center" is robust regardless of content height
// (collapsed vs. expanded items included) — exactly one item is always
// active, no dead zones.
//
// Centering the active item is CSS scroll-snap (`scroll-snap-align:center`
// on .included-item, `scroll-snap-type` on html — see main.css), not JS.
// First cut used a `scroll` listener that, after a pause, computed the
// active item's offset from center and corrected it with
// `window.scrollBy({behavior:"smooth"})` — technically worked, but native
// smooth-scroll firing 160ms after the user's own scroll stopped reads as
// the page fighting them, especially if they start scrolling again just
// as it kicks in. CSS scroll-snap is built for exactly this: it blends
// into the browser's own scroll deceleration instead of layering a
// separate corrective animation on top, and `proximity` (not `mandatory`)
// only engages near a snap point, so it never fights a deliberate scroll
// past one. Sitewide `scroll-snap-type` on `html` is safe — no other page
// has any `scroll-snap-align` targets, so it's a no-op everywhere else.
//
// The image PIN is GSAP ScrollTrigger, not CSS `position:sticky` — that's
// inert here, this site sets `overflow-x:hidden` on both html and body
// (main.css, the fix for a scrollbar-gutter bug), and per spec an
// ancestor with overflow-x non-visible and overflow-y left at its visible
// default computes overflow-y to `auto`, which makes body its own
// scroll-container distinct from the viewport. Confirmed live: sticky
// measured stuck at its static flow position at every scroll offset,
// never actually pinning. ScrollTrigger.pin doesn't have that problem —
// it's what the homepage already uses for this exact "pin an image while
// taller text scrolls past" pattern (Process's beats, Selected Work).
// Pin trigger is "top top" (engages once the list's own top reaches the
// true viewport top) specifically so it lines up with the scroll-snap
// center point — see .included-col/.included-sticky in main.css, which
// center the image within a full-viewport-height column for the same
// reason: image and text center against the same point, not two
// independently-tuned offsets that could drift apart.
function initScrollIndex({ itemSel, imgSel, listSel, stickySel }, root) {
  const items = root.querySelectorAll(itemSel);
  const imgEls = root.querySelectorAll(imgSel);
  const list = root.querySelector(listSel);
  const sticky = root.querySelector(stickySel);
  if (!items.length) return;

  let ticking = false;
  function updateActive() {
    ticking = false;
    // Scoped to when this section is actually near the viewport — this
    // listener is on `window`, so without this guard it kept computing a
    // "closest" item even while the user was scrolling through a
    // completely different section.
    const listRect = list ? list.getBoundingClientRect() : null;
    if (listRect && (listRect.bottom < 0 || listRect.top > window.innerHeight)) return;
    const focusY = window.innerHeight * 0.5;
    let closest = null, closestDist = Infinity;
    items.forEach((el) => {
      const r = el.getBoundingClientRect();
      const dist = Math.abs(r.top + r.height / 2 - focusY);
      if (dist < closestDist) { closestDist = dist; closest = el; }
    });
    if (closest) {
      const idx = closest.dataset.idx;
      items.forEach((el) => el.classList.toggle("is-active", el.dataset.idx === idx));
      imgEls.forEach((el) => el.classList.toggle("is-active", el.dataset.imgIdx === idx));
    }
  }
  window.addEventListener("scroll", () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateActive);
  }, { passive: true });
  updateActive();

  if (list && sticky && window.gsap && window.ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.matchMedia({
      "(min-width: 901px)": function () {
        const st = ScrollTrigger.create({
          trigger: list,
          start: "top top",
          end: "bottom bottom",
          pin: sticky,
          pinSpacing: false,
          // Pure perf tuning, zero visual change — see the identical
          // comment on the homepage's Philosophy pin (motion.js). This is
          // the equivalent real pin:true on service pages.
          anticipatePin: 1,
        });
        return () => st.kill();
      },
    });

    // Real bug, root-caused (pre-Shopify final-polish pass, 2026-08-19):
    // reported as the pinned image "jumping up to the middle rather than
    // easing in" on first load. This ScrollTrigger's start/end pixel
    // values are computed once, at creation time, from whatever the
    // page's layout happens to measure at that instant. Fraunces/Playfair
    // are self-hosted with font-display:swap and no preload hint (same
    // root cause already fixed in the homepage's own Process section,
    // motion.js) — on a cold cache the fallback font is still what's
    // rendering above this pin (hero title, Process beats' own headings/
    // body copy) when this script runs, so the trigger locks in against a
    // too-short page. When the real font swaps in afterward and the page
    // grows taller, this trigger's start/end are now stale — the pin can
    // engage at the wrong scroll position, reading exactly as a jump
    // instead of a smooth pin engagement. motion.js already has the fix
    // for this same class of bug on the homepage (`document.fonts.ready`
    // refresh + a standing ResizeObserver for any other later layout
    // shift — new pins, lazy images, fonts) but this script is separate
    // and never got either. Ported both here.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => ScrollTrigger.refresh());
    }
    let resizeTimer;
    new ResizeObserver(() => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (ScrollTrigger.getAll().some((t) => t.isActive)) return;
        ScrollTrigger.refresh();
      }, 200);
    }).observe(document.body);
  }
}

// ---- Included -> CTA wash — same scroll-scrubbed fade technique as the
// homepage's Process -> Meliza / Reflections -> CTA washes (motion.js),
// reimplemented here since service pages don't load motion.js (they already
// load bare GSAP + ScrollTrigger for initScrollIndex's pin above, so no new
// dependency). Fades #service-included-cta-fade (a .service-included-cta-fade
// div, last child of .service-included) from 0->1 opacity as the section's
// bottom edge approaches the viewport top, so the drop into the CTA band
// reads as a gradual reveal instead of a hard light->dark cut.
function initServiceCtaFade(root) {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reducedMotion || !window.gsap || !window.ScrollTrigger) return;
  const fade = root.querySelector("#service-included-cta-fade");
  const section = root.querySelector(".service-included");
  if (!fade || !section) return;
  gsap.registerPlugin(ScrollTrigger);
  gsap.to(fade, {
    opacity: 1,
    ease: "none",
    // end offset: percentage-of-viewport, same fix as project-template.js's
    // own CTA fade (2026-08-21, superseding an earlier flat-240px attempt
    // that didn't scale to real monitor heights) — see that file's
    // comment for the full reasoning.
    scrollTrigger: {
      trigger: section,
      start: "bottom bottom",
      end: () => "bottom top+=" + Math.round(window.innerHeight * 0.4),
      scrub: true,
    },
  });
}
